import {
    SendHistoryFilterType,
    SendsPageItemType, useInfiniteAccountUsers, useInfiniteSendsPage,
    useInfiniteShortListSendsPage,
    UserDataType
} from "@twigsby/client-data";
import {
    ButtonSubmit,
    ColourSolid,
    ComboBoxControl, DatePicker, FormWrapperLabel,
    Modal,
    ModalType
} from "@twigsby/frontend-framework";
import {FormatIsoDate} from "@twigsby/helpers";
import React, {useEffect, useState} from "react";
import {Controller, FormProvider, useForm} from "@twigsby/frontend-framework/lib/ReactHookForm";
import {useTranslation} from "@twigsby/frontend-framework";
import {useSite} from "../../../Providers/SiteInterfaceProvider";

interface FilterSendsHistoryModalType extends ModalType {
    filterData?: SendHistoryFilterType;
    hidePageDetails?: boolean;
    hideUserDetails?: boolean;
}

const FilterSendsUsers = (props: any) => {
    const {t} = useTranslation();
    const {
        data: usersData,
        error: usersError,
        isLoading: usersLoading
    } = useInfiniteAccountUsers();

    const UserOptions = usersData?.pages.map((page: any) => page?.data && page.data.map((user: UserDataType, index: number) => {
        return { 'label': user.name, 'value': user.userId }
    } ))[0];

    if(!UserOptions) {
        return <></>
    }

    return (
        <ComboBoxControl
            name={'userIds'}
            placeholder={t('controls.usersPlaceholder')}
            data={UserOptions}
            defaultValues={!usersLoading ? props.defaultValues : null}
            loading={usersLoading}
            disabled={!UserOptions}
            valueType={'string'}
            forwardValuesCallback={(e) => props.changeCallback(e)}
        />
    )
}

const FilterSendsPages = (props: any) => {
    const {t} = useTranslation();
    const {
        data: pagesData,
        error: pagesError,
        isLoading: pagesLoading
    } = useInfiniteSendsPage();

    const PagesOptions =
        pagesData?.pages.map((page: any) => page?.data && page.data.map((page: SendsPageItemType, index: number) => {
            return { 'label': page.title, 'value': page.id }
        } ))[0];

    if(!PagesOptions) {
        return <></>
    }

    return (
        <ComboBoxControl
            name={'pageIds'}
            placeholder={t('controls.sendsProfilesPlaceholder')}
            data={PagesOptions}
            defaultValues={!pagesLoading ? props.defaultValues : null}
            loading={pagesLoading}
            disabled={!PagesOptions}
            valueType={'number'}
            forwardValuesCallback={(e) => props.changeCallback(e)}
        />
    )
}

const FilterSendsPagesUser = (props: any) => {
    const{ t } = useTranslation();
    const {
        data: pagesShortData,
        error: pagesShortError,
        isLoading: pagesShortLoading
    } = useInfiniteShortListSendsPage();

    const PagesOptions =
        pagesShortData?.pages.map((page: any) => page?.data && page.data.map((page: SendsPageItemType, index: number) => {
            return { 'label': page.title, 'value': page.id }
        } ))[0];

    if(!PagesOptions) {
        return <></>
    }

    return (
        <ComboBoxControl
            name={'pageIds'}
            placeholder={t('controls.sendsProfilesPlaceholder')}
            data={PagesOptions}
            defaultValues={!pagesShortLoading ? props.defaultValues : null}
            loading={pagesShortLoading}
            disabled={!PagesOptions}
            valueType={'number'}
            forwardValuesCallback={(e) => props.changeCallback(e)}
        />
    )
}

const FilterSendsHistoryModal = (props: FilterSendsHistoryModalType) => {
    const { t } = useTranslation();
    const{ currentLocale } = useSite();

    const formMethods = useForm({
        defaultValues: {
            pageIds: props.filterData?.pageIds || undefined,
            userIds: props.filterData?.userIds || undefined,
            dateRange: {
                startDate: props.filterData?.startDate ? new Date(props.filterData?.startDate) : new Date(),
                endDate: props.filterData?.endDate ? new Date(props.filterData?.endDate) : new Date()
            }
        }
    });

    const { control, handleSubmit, formState, reset } = formMethods;



    const HandleFilterChange = (data: any) => {
        let newData: SendHistoryFilterType = { }

        if(data.pageIds?.length > 0) {
            newData.pageIds = data.pageIds;
        }

        if(data.userIds?.length > 0) {
            newData.userIds = data.userIds;
        }

        if(data.dateRange) {
            newData.startDate = FormatIsoDate(data.dateRange?.startDate);
            newData.endDate = FormatIsoDate(data.dateRange?.endDate);
        }

        if(props.actionedCallback) {
            props.actionedCallback(newData);
        }
    }

    return (
        <Modal title={t('sends.profilesHistory.filter.title')} isOpen={props.isOpen} closeCallback={props.closeCallback}>

            <FormProvider {...formMethods}>
                <form onSubmit={handleSubmit((data: any) => HandleFilterChange(data))}>
                    <fieldset className={'flex-column flex-gap-md'}>

                        {!props.hidePageDetails && (
                            <Controller
                                control={control}
                                name='pageIds'
                                render={({ field, fieldState }) =>
                                    <FilterSendsPages
                                        defaultValues={field.value}
                                        changeCallback={(e: any) => field.onChange(e)}
                                    />
                                }
                            />
                        )}

                        {!props.hideUserDetails && (
                            <Controller
                                control={control}
                                name='userIds'
                                render={({ field, fieldState }) =>
                                    <FilterSendsPagesUser
                                        defaultValues={field.value}
                                        changeCallback={(e: any) => field.onChange(e)}
                                    />
                                }
                            />
                        )}

                        <FormWrapperLabel label={t('sends.profilesHistory.filter.labels.dateRange')}>
                            <Controller
                                control={control}
                                name='dateRange'
                                render={({ field, fieldState }) => {
                                    console.log(field);
                                    return (
                                        <DatePicker
                                            {...field}
                                            showDateDisplay={false}
                                            maxDate={new Date()}
                                            editableDateInputs={true}
                                            forwardValuesCallback={(dates) => {
                                                field.onChange(dates);
                                            }}
                                            locale={currentLocale}
                                            moveRangeOnFirstSelection={false}
                                            defaultStartDate={field.value.startDate}
                                            defaultEndDate={field.value.endDate}
                                        />
                                    )}
                                } />
                        </FormWrapperLabel>

                        <ButtonSubmit fullWidth={true} colour={ColourSolid.Primary} title={t('actions.filter')} disabled={!formState.isValid} />

                    </fieldset>
                </form>
            </FormProvider>

        </Modal>
    )
}

export default FilterSendsHistoryModal
